// Jurisdiction Deadlines — the statutory appeal-deadline rules that drive Calendar.
//
// ⛔ THE ONLY CLIENT-INDEPENDENT REPORT. It has no client, no years, no parcels and no
// representation gate, because it is REFERENCE data — the rules themselves, not anybody's
// portfolio. That needed no change to the layer: ReportView derives `wantsClient` and `wantsYears`
// from `definition.filters`, so omitting them renders no client picker and no years bar and
// fetch(ctx) simply runs with an empty clientId. The one thing it did need was a 'link' column
// type, because formatCell returns a string and a clickable source URL cannot come out of it.
//
// ⛔ IT EXISTS BECAUSE NOTHING COULD SEE THIS TABLE. 63 rules covering 50 states plus DC and PR,
// read by exactly one consumer (src/utils/deadlineRules.js) and displayed by none. They also sat
// RLS deny-all from March to 2026-08-08, so every deadline the app computed in that window came
// back blank — see 2026-08-08_rls_batch9_jurisdiction_deadlines.sql.
//
// ⛔ THE POINT OF THE REPORT IS THE VERIFICATION SPLIT. 25 of the 63 rules have never been checked
// against a statute. An unverified rule does not produce a blank, it produces a CONFIDENT WRONG
// DATE, which is worse. That number leads the summary rather than sitting in a footnote.

import { fetchAll } from '../pagination';
import { describeRule } from '../../utils/deadlineRules';
import { assertRowFields, warnOnDeadFilters } from '../grouping';

// ⛔ 365 days, counted in LOCAL time from a bare 'YYYY-MM-DD'. Never `new Date(str)` — that reads a
// date-only string as UTC midnight and shifts it a day backwards for anyone west of Greenwich,
// which on a staleness boundary flips a rule between buckets for no reason.
const STALE_DAYS = 365;

// ⛔ NORMALISE A date COLUMN TO 'YYYY-MM-DD' AT THE BOUNDARY, ACCEPTING BOTH SHAPES.
// supabase-js hands back a `date` as a STRING ('2026-05-13'), which is what runs in production.
// A raw Postgres driver hands back a Date pinned to UTC MIDNIGHT, and reading LOCAL components off
// that shifts it a day backwards for anyone west of Greenwich — verified: 2026-05-13 read as
// 2026-05-12 in a US timezone. That day is not cosmetic here; it moves a rule across the 365-day
// staleness boundary and mislabels the date on screen. So a Date is read in UTC (a Postgres `date`
// has no zone; UTC midnight is the driver's encoding of the calendar day, not a moment in time),
// and everything downstream sees one canonical string.
function toDateOnly(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return null;
    return `${v.getUTCFullYear()}-${String(v.getUTCMonth() + 1).padStart(2, '0')}-${String(v.getUTCDate()).padStart(2, '0')}`;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function parseLocalDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(toDateOnly(v) ?? ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}
function daysSince(v) {
  const d = parseLocalDate(v);
  if (!d) return null;
  const today = new Date();
  return Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - d) / 86400000);
}

// ⛔ THREE STATES, NOT TWO — the distinction the whole report turns on.
//   Verified       checked against a statute within the last year
//   Stale          checked, but over a year ago — the citation may have moved
//   Never verified no one has ever checked it. NOT a very old verification; a different claim.
// Collapsing the last two would bury 25 unchecked rules inside a "needs attention" number that
// also contains rules someone did read. On today's data Stale is EMPTY (all 38 verified rows were
// checked 2026-05-13), and a zero there is the correct answer, not a broken query.
export const VERIFIED = 'Verified';
export const STALE = 'Stale';
export const NEVER = 'Never verified';
const verificationOf = (r) => {
  const n = daysSince(r.last_verified_date);
  if (n == null) return NEVER;
  return n > STALE_DAYS ? STALE : VERIFIED;
};

const asOfLabel = (d = new Date()) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const titleRows = (heading) => [null, 'National Tax Resource Group', heading, `As of ${asOfLabel()}`, null, null];

export const jurisdictionDeadlines = {
  id: 'jurisdiction_deadlines',
  title: 'Jurisdiction Deadlines',
  description: 'The statutory appeal-deadline rules behind every date Calendar computes.',

  // ⛔ NO 'client' AND NO 'years'. Their absence is what makes this report client-independent —
  // see the note at the top. Do not add them "for consistency".
  filters: [
    { key: 'state', type: 'enum', label: 'State', from: 'state' },
    { key: 'rule_type', type: 'enum', label: 'Rule type', from: 'rule_type' },
    { key: 'verification', type: 'enum', label: 'Verification', from: 'verification' },
    { key: 'portfolio', type: 'enum', label: 'Portfolio', from: 'portfolio' },
    { key: 'q', type: 'search', label: 'Search', keys: ['jurisdiction', 'state', 'notes', 'rule'] },
  ],

  columns: [
    // Locked: State and Jurisdiction identify a rule row.
    { key: 'state', label: 'State', type: 'text', locked: true },
    { key: 'jurisdiction', label: 'Jurisdiction', type: 'text', locked: true },
    { key: 'rule_type', label: 'Rule Type', type: 'text' },
    // The English rendering, from deadlineRules.describeRule — the same ladder Calendar runs.
    { key: 'rule', label: 'Deadline Rule', type: 'text' },
    { key: 'fixed_month', label: 'Fixed Month', type: 'int' },
    { key: 'fixed_day', label: 'Fixed Day', type: 'int' },
    { key: 'notice_plus_days', label: 'Notice + Days', type: 'int' },
    { key: 'verification', label: 'Verification', type: 'text' },
    { key: 'last_verified_date', label: 'Last Verified', type: 'date' },
    { key: 'verified_by', label: 'Verified By', type: 'text' },
    { key: 'portfolio', label: 'Portfolio', type: 'text' },
    { key: 'notes', label: 'Notes', type: 'text' },
    { key: 'source_url', label: 'Source', type: 'link' },
    // ⛔ The raw JSONB, OFF by default. The English column is the readable one and cannot drift
    // from Calendar; this is here so nothing is lost in an export and so a formula can be checked
    // literally when the English reads oddly.
    { key: 'complex_rule_formula', label: 'Complex Rule Formula (raw)', type: 'text', optional: true },
  ],

  // ⛔ `.eq('is_active', true)` — IDENTICAL to deadlineRules.js:31. is_active is NULLABLE, so a
  // null is neither active nor inactive and this test excludes it. Any looser test here (`is not
  // false`) would show rules Calendar ignores, and the two screens would disagree about which
  // rules are in force.
  //
  // Paginated even though the table is 63 rows: it is a `fetchAll` either way, and a report that
  // silently truncates the day someone adds a thousandth rule is the failure this layer keeps
  // paying for. The parcel read genuinely needs it — that one is thousands of rows.
  async fetch() {
    const [rules, parcels] = await Promise.all([
      fetchAll('jurisdiction_deadlines', '*', (q) => q.eq('is_active', true)),
      // ⛔ Which states we actually hold property in. A never-verified rule for a state with no
      // portfolio is a backlog item; one for a state we file in is a live risk, and that is the
      // cut that makes 25 actionable instead of merely alarming.
      //
      // Deliberately NOT expected_bills: that RPC returns six rows for 2026 because it inner-joins
      // through parcel_collectors, the collector's value_year and an instalment schedule, so it
      // answers "which parcels are configured for billing", not "where do we hold property".
      // Reading parcels directly cannot be gated by collector configuration.
      //
      // retired_year is the FIRST year NOT represented, so `is null` means never retired. A parcel
      // retired and later reactivated carries both, and reactivated_year being set means it is
      // ours again — hence the second arm.
      fetchAll('parcels', 'state,retired_year,reactivated_year'),
    ]);
    const held = new Set(
      (parcels || [])
        .filter((p) => p.state && (p.retired_year == null || p.reactivated_year != null))
        .map((p) => String(p.state).trim().toUpperCase())
    );
    return { rules, held };
  },

  buildRows(raw) {
    const { rules = [], held = new Set() } = raw || {};
    const rows = rules.map((r) => ({
      __key: r.id,
      state: r.state || '',
      jurisdiction: r.jurisdiction || '',
      rule_type: r.rule_type || '',
      // ⛔ null here is a FINDING, not a formatting gap: describeRule returns null exactly when
      // applyRule would produce no date. Surfaced as an explicit statement rather than a blank,
      // because a blank in this column reads as "we didn't render it" when it means "this rule
      // computes nothing".
      rule: describeRule(r) || 'NO DEADLINE COMPUTED',
      fixed_month: r.fixed_month ?? null,
      fixed_day: r.fixed_day ?? null,
      notice_plus_days: r.notice_plus_days ?? null,
      verification: verificationOf(r),
      // Canonicalised, so the displayed date and the staleness bucket read the same value.
      last_verified_date: toDateOnly(r.last_verified_date),
      verified_by: r.verified_by ?? null,
      portfolio: held.has(String(r.state || '').trim().toUpperCase()) ? 'Held' : 'None',
      notes: r.notes ?? null,
      source_url: r.source_url ?? null,
      complex_rule_formula: r.complex_rule_formula ? JSON.stringify(r.complex_rule_formula) : null,
    }));
    assertRowFields(rows, ['state', 'jurisdiction', 'rule_type', 'verification', 'portfolio', 'rule'], 'jurisdiction deadlines');
    warnOnDeadFilters(rows, this.filters, 'jurisdiction deadlines');
    // ⛔ Sorted here, not via defaultSort, which takes a single key. State then jurisdiction, so
    // NJ's five rules and NY's four read as a block.
    rows.sort((a, b) => String(a.state).localeCompare(String(b.state))
      || String(a.jurisdiction).localeCompare(String(b.jurisdiction)));
    return rows;
  },

  // ⛔ THE NEVER-VERIFIED COUNT LEADS, and it is split by whether we hold property in that state,
  // because those two numbers mean different things. Computed from the FILTERED rows, so narrowing
  // the table moves them with it.
  summary(rows) {
    const list = rows || [];
    if (!list.length) return [];
    const count = (p) => list.filter(p).length;
    const never = count((r) => r.verification === NEVER);
    const neverHeld = count((r) => r.verification === NEVER && r.portfolio === 'Held');
    const stale = count((r) => r.verification === STALE);
    const noDeadline = count((r) => r.rule === 'NO DEADLINE COMPUTED');
    const pct = (n) => (list.length ? `${Math.round((n / list.length) * 100)}%` : '—');

    const byType = new Map();
    list.forEach((r) => byType.set(r.rule_type || '(none)', (byType.get(r.rule_type || '(none)') || 0) + 1));

    return [
      { label: 'Rules', value: String(list.length), sub: `${new Set(list.map((r) => r.state)).size} states` },
      // ⛔ Printed even at zero — "0 never verified" is the answer we want to be able to reach.
      { label: 'Never verified', value: String(never), sub: `${pct(never)} of the rules shown` },
      { label: '…in states we hold', value: String(neverHeld),
        sub: neverHeld ? 'live risk — a wrong date reaches a real parcel' : 'none of them reach a parcel' },
      { label: 'Stale (>1 yr)', value: String(stale), sub: stale ? 'citation may have moved' : 'nothing over a year old' },
      { label: 'No deadline computed', value: String(noDeadline),
        sub: noDeadline ? 'rule exists, Calendar returns nothing' : 'every rule resolves' },
      ...[...byType.entries()].sort((a, b) => b[1] - a[1]).map(([t, n]) => ({ label: t, value: String(n), sub: 'rules' })),
    ];
  },

  exportSheets({ rows, columns, footnotes }) {
    // By State: one line per state with its rule count and how much of it is unchecked. The flat
    // Detail sheet already carries every rule; this is the shape someone scans to decide where to
    // start, which a flat table cannot be — ReportView renders one table by design.
    const byState = new Map();
    (rows || []).forEach((r) => {
      const g = byState.get(r.state) || { state: r.state, rules: 0, never: 0, stale: 0, noDeadline: 0, portfolio: r.portfolio, jurisdictions: [] };
      g.rules += 1;
      if (r.verification === NEVER) g.never += 1;
      if (r.verification === STALE) g.stale += 1;
      if (r.rule === 'NO DEADLINE COMPUTED') g.noDeadline += 1;
      g.jurisdictions.push(r.jurisdiction);
      byState.set(r.state, g);
    });
    const stats = [...byState.values()]
      // ⛔ localeCompare, matching buildRows — a bare .sort() compares UTF-16 code units, which puts
      // 'NYC (Class 1)' before 'Nassau County' because 'Y' (89) < 'a' (97). The table and this sheet
      // would then list NY's four rules in two different orders in one report.
      .map((g) => ({ ...g, jurisdictions: [...g.jurisdictions].sort((x, y) => String(x).localeCompare(String(y))).join(', ') }))
      .sort((a, b) => b.never - a.never || String(a.state).localeCompare(String(b.state)));

    return [
      {
        name: 'By State',
        titleRows: titleRows('Jurisdiction Deadlines — by State'),
        columns: [
          { key: 'state', label: 'State', type: 'text' },
          { key: 'portfolio', label: 'Portfolio', type: 'text' },
          { key: 'rules', label: 'Rules', type: 'int' },
          { key: 'never', label: 'Never Verified', type: 'int' },
          { key: 'stale', label: 'Stale', type: 'int' },
          { key: 'noDeadline', label: 'No Deadline', type: 'int' },
          { key: 'jurisdictions', label: 'Jurisdictions', type: 'text' },
        ],
        rows: stats,
        totals: {
          state: 'TOTAL',
          rules: stats.reduce((s, g) => s + g.rules, 0),
          never: stats.reduce((s, g) => s + g.never, 0),
          stale: stats.reduce((s, g) => s + g.stale, 0),
          noDeadline: stats.reduce((s, g) => s + g.noDeadline, 0),
        },
        footnotes,
      },
      { name: 'Detail', titleRows: titleRows('Jurisdiction Deadlines'), columns, rows, footnotes },
    ];
  },

  emptyMessage:
    'No rules match this selection. This report shows only ACTIVE rules (is_active = true), which is '
    + 'the same set Calendar uses. Clear the state, rule-type or verification filters, or check '
    + 'whether the jurisdiction_deadlines table is readable — it sat RLS deny-all from March to '
    + 'August 2026, and an empty result looked exactly like this.',
};

export default jurisdictionDeadlines;
